import { useEffect, useId, useRef, useState } from "react";
import {
  ComplaintItemViewModel,
  ComplaintServerVM,
  ViewModel,
  complaintViewModelToServerModel,
} from "./ViewModel";
import getViewModelNewValues from "./ViewModelNewValues";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { httpPost } from "../../Common/Http/httpPost";
import getClassMetadata from "../../Common/classMetadataService";
import LoadingContentWrapper from "../../Common/Loading/LoadingContentWrapper";
import TextInputFormRow from "../../Common/EditForm/TextInput/TextInputFormRow";
import getTextMetadata from "../../Common/EditForm/ClassMetadata/getTextMetadata";
import { AttachmentUpload } from "./controls/AttachmentUpload";
import FormButtons from "../../Common/EditForm/formButtons";
import SaveError from "../../Common/EditForm/SaveError";
import { ConfirmDialog } from "../../Common/Dialog/ConfirmDialog";
import httpPostNoBody from "../../Common/Http/httpPostNoBody";
import SaveSuccess from "./controls/SaveSuccess";
import ComplaintItemForm, {
  complaintReasonId_TransportDamage,
} from "./ComplaintItemForm";
import ComplaintItemDisplayFragment from "./controls/ComplaintItemDisplayFragment";
import { AlertDialog } from "../../Common/Dialog/AlertDialog";
import BusinessDocumentSelectorFormRow, {
  BusinessDocumentSelectorHandle,
} from "../../BusinessDocuments/BusinessDocumentSelector/BusinessDocumentSelectorFormRow";
import Label from "../../Common/EditForm/Label";
import businessDocumentVM from "../../BusinessDocuments/BusinessDocumentSelector/businessDocumentVM";
import { DateTime } from "luxon";
import selectorSection from "../../BusinessDocuments/BusinessDocumentSelector/selectorSection";
import "../../styles/fakeDisabled.scss";

export default function CustomerComplaintForm(props: {
  navigate: (url: string) => void;
  backUrl?: string;
  onOk?: (id: number, name: string) => void;
  onCancel?: () => void;
}) {
  let [isLoading, setIsLoading] = useState<boolean>(true);
  let [isSaveError, setIsSaveError] = useState<boolean>(false);
  let [isSaveSuccessful, setIsSaveSuccessful] = useState<boolean>(false);
  let [metadata, setMetadata] = useState<{ [key: string]: any }>({});

  let [isItemFormShown, setIsItemFormShown] = useState<boolean>(false);
  let [editedItem, setEditedItem] = useState<ComplaintItemViewModel | null>(
    null
  );
  let [editedItemIndex, setEditedItemIndex] = useState<number>(0);
  let [isAttachmentRequired, setIsAttachmentRequired] =
    useState<boolean>(false);
  const [salesDocumentTypeName, setSalesDocumentTypeName] =
    useState<string>("");
  const [salesDate, setSalesDate] = useState<string>("");

  const methods = useForm<ViewModel>({
    mode: "onTouched",
    defaultValues: getViewModelNewValues(),
  });

  const { handleSubmit, formState, control, getValues, reset } = methods;

  const businessDocumentSelectorRef =
    useRef<BusinessDocumentSelectorHandle>(null);

  const itemsMethods = useFieldArray<ViewModel>({
    control,
    name: "complaintItems",
    rules: {},
  });

  const isThereTransportDamage = (
    itemsMethods.fields as ComplaintItemViewModel[]
  ).some((x) => x.complaintTypeId === complaintReasonId_TransportDamage);
  if (isThereTransportDamage !== isAttachmentRequired)
    setIsAttachmentRequired(isThereTransportDamage);

  /** True when there are any lines in this complaint */
  const areThereAnyItems =
    (itemsMethods.fields as ComplaintItemViewModel[]).length > 0;

  const { isSubmitting } = formState;

  const onSubmit = async (values: ViewModel) => {
    if (values.complaintItems.length === 0) {
      await AlertDialog(
        "Brak pozycji",
        "Musisz wprowadzić co najmniej jedną pozycję zgłoszenia."
      );
      return;
    }

    if (isAttachmentRequired && values.attachments.length === 0) {
      await AlertDialog(
        "Brak załącznika",
        "Ponieważ zgłosiłeś reklamację z powodu szkody transportowej, musisz dodać załącznik z protokołem szkody."
      );
      return;
    }

    // confirm submission

    let confirm = await ConfirmDialog(
      "Potwierdzenie wysyłania formularza",
      "Czy chcesz zakończyć wypełnianie formularza reklamacji i wysłać go?\r\nWysłanego zgłoszenia nie można wycofać ani edytować."
    );

    if (confirm === false) return;

    // let vmToSave = Object.assign({}, viewModel, values); // if values contain all the necessary data, this step will be unnecessary
    let data = complaintViewModelToServerModel(values);

    try {
      /* let o = */ await httpPost<ComplaintServerVM, any>(
        "/api/Complaints/Complaint",
        data
      );
      setIsSaveSuccessful(true);
    } catch (e) {
      setIsSaveError(true);
    }

    return;
  };

  const onInitialize = async () => {
    // load form values
    // fetch existing data for the form
    // load data for selects, etc.
    // this method is async so we can use await here

    let metadata_response = await getClassMetadata("ComplaintVM");
    setMetadata(metadata_response);

    reset(getViewModelNewValues());

    setIsLoading(false);
  };

  useEffect(() => {
    onInitialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCancel = async () => {
    let r = await ConfirmDialog(
      "Anulowanie wysyłania",
      "Czy na pewno chcesz porzucić ten formularz?\r\nWybierz OK aby porzucić formularz."
    );
    if (r === true) {
      let attachments = getValues("attachments");
      for (let attachement of attachments) {
        try {
          let fileId = attachement.id;
          await httpPostNoBody(
            `/api/Attachment/AttachmentUpload/removeUploadedFile/${fileId}`
          );
        } catch {
          // silently ignore a failure to remove an attachment, it will be deleted anyway.
        }
      }
      props.navigate("./../index");
    }
  };

  // const onFailure = async () => {
  //   reset(getViewModelNewValues());
  //   setIsSaveError(false);
  // };

  const onSaveSuccess = async () => {
    props.navigate("./../index");
  };

  const onRemoveItem = async (index: number) => {
    let r = await ConfirmDialog(
      "Usunięcie pozycji",
      `Czy na usunąć pozycję ${index + 1} ze zgłoszenia?`
    );
    if (r === true) {
      itemsMethods.remove(index);
    }
  };

  const onEditItem = async (index: number) => {
    const item = getValues().complaintItems[index];
    setEditedItem(item);
    setEditedItemIndex(index);
    setIsItemFormShown(true);
  };

  const onNewItem = async () => {
    setEditedItem(null);
    setIsItemFormShown(true);
  };

  const onItemSave = async (values: ComplaintItemViewModel) => {
    if (editedItem !== null) {
      // this was an edit
      itemsMethods.update(editedItemIndex, values);
    } else {
      // this was a new item
      itemsMethods.append(values!);
    }
    setIsItemFormShown(false);
  };

  const salesDocumentTypeNameId = useId();
  const salesDateId = useId();

  const onDocumentChanged = (businessDocument: businessDocumentVM) => {
    if (!businessDocument) {
      setSalesDocumentTypeName("");
      setSalesDate("");
    } else {
      setSalesDocumentTypeName(businessDocument.businessDocumentName);
      setSalesDate(
        DateTime.fromJSDate(businessDocument.salesDateAsDateOnly!).toFormat(
          "yyyy-MM-dd"
        )
      );
    }
  };

  return (
    <>
      {!isItemFormShown && (
        <div className="content-wrapper">
          <h1 className="border-bottom pb-2 mt-4 mb-5">Formularz reklamacji</h1>
          <LoadingContentWrapper isLoading={isLoading}>
            {!isLoading && !isSaveError && !isSaveSuccessful && (
              <FormProvider {...methods}>
                <form noValidate>
                  <TextInputFormRow
                    {...getTextMetadata<ViewModel>(
                      "customerRepName",
                      metadata["customerRepName"]
                    )}
                    label="Imię i nazwisko *"
                    longDescription="Imię i nazwisko osoby składającej reklamację (wymagane)."
                    control={control}
                    inlineDescription={undefined}
                  />

                  <TextInputFormRow
                    {...getTextMetadata<ViewModel>(
                      "customerRepEMail",
                      metadata["customerRepEMail"]
                    )}
                    label="Adres e-mail *"
                    longDescription="Adres e-mail osoby składającej reklamację (wymagane)."
                    control={control}
                    inlineDescription={undefined}
                  />

                  <TextInputFormRow
                    {...getTextMetadata<ViewModel>(
                      "customerRepPhone",
                      metadata["customerRepPhone"]
                    )}
                    label="Numer telefonu"
                    longDescription="Numer telefonu osoby składającej reklamację (niewymagane)."
                    control={control}
                    inlineDescription={undefined}
                    className="-app-medium-control"
                  />

                  <BusinessDocumentSelectorFormRow
                    label="Numer dokumentu sprzedaży *"
                    name="salesDocument"
                    id="salesDocument"
                    control={control}
                    longDescription="Numer faktury VAT lub dokumentu Wz, którego dotyczą towary do reklamacji (wymagane)."
                    required={true}
                    selectorSectionId={selectorSection.complaints}
                    ref={businessDocumentSelectorRef}
                    locked={areThereAnyItems}
                    onDocumentChanged={onDocumentChanged}
                  />

                  {areThereAnyItems && (
                    <div className="row mb-1">
                      <div className="col-sm-8 offset-sm-4">
                        <div
                          className="alert alert-primary d-flex align-items-center"
                          role="alert"
                        >
                          <div className="flex-shrink-0 ms-3 me-4">
                            <i className="bi bi-lightbulb"></i>
                          </div>
                          <div>
                            Nie możesz zmienić dokumentu sprzedaży, ponieważ
                            dodałeś już pozycję reklamacji odnoszącą się do
                            niego.
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="row mb-3">
                    <Label
                      label="Rodzaj dokumentu sprzedaży"
                      id={salesDocumentTypeNameId}
                      isError={false}
                      description={
                        "Rodzaj dokumentu, zostanie uzupełniony automatycznie z wybranego dokumentu"
                      }
                    ></Label>
                    <div className="col-sm-8">
                      <input
                        tabIndex={-1}
                        onMouseDown={(e) => e.preventDefault()}
                        readOnly={true}
                        aria-disabled="true"
                        onClick={() =>
                          businessDocumentSelectorRef.current?.openSelector()
                        }
                        id={salesDocumentTypeNameId}
                        className="form-control -app-medium-control -app-fake-disabled"
                        type="text"
                        value={salesDocumentTypeName}
                        name="salesDocumentTypeName"
                        placeholder="Wybierz dokument"
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <Label
                      label="Data sprzedaży"
                      id={salesDateId}
                      isError={false}
                      description={
                        "Data sprzedaży, zostanie uzupełniona automatycznie z wybranego dokumentu"
                      }
                    ></Label>
                    <div className="col-sm-8">
                      <input
                        tabIndex={-1}
                        onMouseDown={(e) => e.preventDefault()}
                        readOnly={true}
                        aria-disabled="true"
                        onClick={() =>
                          businessDocumentSelectorRef.current?.openSelector()
                        }
                        id={salesDateId}
                        className="form-control -app-small-control -app-fake-disabled"
                        type="text"
                        value={salesDate}
                        name="salesDate"
                        placeholder="Wybierz dokument"
                      />
                    </div>
                  </div>
                  <hr />
                  <h2
                    className="mb-3"
                    title="Poniżej wprowadź pozycje dokumentu, których dotyczy reklamacja."
                  >
                    Pozycje
                    <sup>
                      <i className="bi bi-question-circle -app-h2-section-label-help"></i>
                    </sup>
                  </h2>

                  <ComplaintItemDisplayFragment
                    items={itemsMethods.fields as ComplaintItemViewModel[]}
                    onRemoveItem={onRemoveItem}
                    onEditItem={onEditItem}
                    onNewItem={onNewItem}
                    isNewItemEnabled={!!getValues("salesDocument")}
                    onOpenDocumentSelector={() =>
                      businessDocumentSelectorRef.current?.openSelector()
                    }
                  ></ComplaintItemDisplayFragment>

                  <hr />
                  <h2
                    className="mb-3"
                    title="Poniżej możesz dodać załączniki i podać ich opis."
                  >
                    Załączniki
                    <sup>
                      <i className="bi bi-question-circle -app-h2-section-label-help"></i>
                    </sup>
                  </h2>

                  <AttachmentUpload
                    parent="attachments"
                    isTransportDamageAttachmentRequired={isAttachmentRequired}
                  ></AttachmentUpload>

                  <hr />

                  <div className="row pt-2">
                    <div className="offset-sm-4 col-sm-8">
                      <FormButtons
                        onSave={(e) => handleSubmit(onSubmit)(e)}
                        onCancel={(e) => onCancel()}
                        isSubmitting={isSubmitting}
                      ></FormButtons>
                    </div>
                  </div>
                </form>
              </FormProvider>
            )}
            {isSaveError && (
              <SaveError
                navigate={props.navigate}
                backUrl="./../index"
              ></SaveError>
            )}
            {isSaveSuccessful && <SaveSuccess done={() => onSaveSuccess()} />}
          </LoadingContentWrapper>
        </div>
      )}

      {isItemFormShown && (
        <ComplaintItemForm
          values={editedItem}
          onOk={(values: ComplaintItemViewModel) => onItemSave(values)}
          onCancel={() => {
            setIsItemFormShown(false);
          }}
          businessDocumentType={
            getValues("salesDocument")?.businessDocumentType || 1
          }
          documentNumber={getValues("salesDocument")?.documentNumber || ""}
        ></ComplaintItemForm>
      )}
    </>
  );
}
